import React, { useMemo } from 'react';
import type { TabDef } from './tabRegistry.js';
import { TAB_GROUPS } from './tabRegistry.js';

interface AppShellProps {
  tabs: TabDef[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  children: React.ReactNode;
}

/**
 * Manager portal shell. Desktop: fixed left sidebar (grouped) + scrollable
 * main column. Below lg: sidebar collapses to a horizontal scrolling strip
 * above the content so narrow viewports stay usable.
 */
export default function AppShell({ tabs, activeTab, onTabChange, children }: AppShellProps) {
  // Group tabs once per tabs change so rendering is stable across keystrokes.
  const grouped = useMemo(() => {
    return TAB_GROUPS.map((group) => ({
      group,
      items: tabs.filter((tab) => tab.group === group.id),
    })).filter((g) => g.items.length > 0);
  }, [tabs]);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[240px_minmax(0,1fr)]">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:border-r lg:border-white/10 lg:h-screen lg:sticky lg:top-0">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="text-eyebrow">Prestige World Drive</div>
          <div className="text-h3 mt-1">Manager Portal</div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-6">
          {grouped.map(({ group, items }) => (
            <div key={group.id}>
              <div className="px-2 pb-2 text-eyebrow">{group.label}</div>
              <ul className="flex flex-col gap-1">
                {items.map((tab) => {
                  const active = tab.id === activeTab;
                  return (
                    <li key={tab.id}>
                      <SidebarItem tab={tab} active={active} onSelect={onTabChange} />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Compact nav strip — mobile/tablet fallback */}
      <nav className="lg:hidden border-b border-white/10">
        <div className="flex gap-2 overflow-x-auto px-4 py-3">
          {tabs.map((tab) => {
            const active = tab.id === activeTab;
            const base = 'shrink-0 rounded-lg px-3.5 py-2 text-sm cursor-pointer transition-colors border whitespace-nowrap inline-flex items-center gap-2';
            const state = active
              ? 'border-[color:var(--color-gold)]/60 bg-[color:var(--color-gold)]/10 text-[color:var(--color-gold)]'
              : 'border-white/10 bg-white/[0.03] text-white';
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`${base} ${state}`}
              >
                {tab.icon && (
                  <span aria-hidden="true" className="text-base leading-none">{tab.icon}</span>
                )}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Main content */}
      <main className="min-w-0">
        <header className="border-b border-white/10">
          <div className="mx-auto w-full max-w-7xl px-6 py-5">
            <div className="text-eyebrow">Operations Console</div>
            <h1 className="text-h1 mt-2">
              {tabs.find((t) => t.id === activeTab)?.label || 'Overview'}
            </h1>
          </div>
        </header>
        <div className="mx-auto w-full max-w-7xl px-6 py-6">
          {children}
        </div>
      </main>
    </div>
  );
}

interface SidebarItemProps {
  tab: TabDef;
  active: boolean;
  onSelect: (tabId: string) => void;
}

function SidebarItem({ tab, active, onSelect }: SidebarItemProps) {
  const base =
    'relative w-full text-left rounded-lg pl-4 pr-3 py-2 text-sm transition-colors flex items-center gap-3 border border-transparent';
  const state = active
    ? 'bg-[color:var(--color-gold)]/10 text-[color:var(--color-gold)] border-[color:var(--color-gold)]/30'
    : 'text-white/85 hover:bg-white/[0.04] hover:text-white';
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={() => onSelect(tab.id)}
      className={`${base} ${state}`}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[color:var(--color-gold)]"
        />
      )}
      <span aria-hidden="true" className="w-5 shrink-0 text-center text-base leading-none">
        {tab.icon || ''}
      </span>
      <span className="flex-1 truncate">{tab.label}</span>
    </button>
  );
}
